<template>
  <div>
    <div style="margin-bottom: 12px;">
      <el-radio-group v-model="sourceType" @change="reload">
        <el-radio-button label="game">游戏文章</el-radio-button>
        <el-radio-button label="collection">合集文章</el-radio-button>
        <el-radio-button label="category">分类文章</el-radio-button>
        <el-radio-button label="site">全局站内文章</el-radio-button>
      </el-radio-group>
    </div>
    <div style="display: flex; gap: 8px; margin-bottom: 12px; flex-wrap: wrap;">
      <el-input v-if="sourceType === 'game'" v-model="query.gameId" placeholder="Game ID" style="width: 180px" @keyup.enter="reload" />
      <el-input v-else-if="sourceType === 'collection'" v-model="query.collectionId" placeholder="合集ID" style="width: 180px" @keyup.enter="reload" />
      <el-input v-else-if="sourceType === 'category'" v-model="query.categoryName" placeholder="分类名称" style="width: 180px" @keyup.enter="reload" />
      <el-input v-else v-model="query.siteName" placeholder="文章名称（英文）" style="width: 200px" @keyup.enter="reload" />
      <el-input v-model="query.title" placeholder="标题搜索" style="width: 220px" @keyup.enter="reload" />
      <el-button type="primary" :loading="loading" @click="reload">查询</el-button>
      <el-button :loading="loading" @click="openDialog()">新建文章</el-button>
    </div>

    <el-table v-loading="loading" :data="rows">
      <el-table-column v-if="sourceType === 'game'" prop="gameId" label="Game ID" width="160" />
      <el-table-column v-else-if="sourceType === 'collection'" prop="collectionId" label="合集ID" width="160" />
      <el-table-column v-else-if="sourceType === 'category'" prop="categoryName" label="分类名称" width="160" />
      <el-table-column v-else prop="name" label="文章名称" width="160" />
      <el-table-column v-if="sourceType === 'site'" prop="contentType" label="内容类型" width="110" />
      <el-table-column prop="title" label="标题" min-width="220" />
      <el-table-column prop="updatedAt" label="更新时间" width="200" />
      <el-table-column label="操作" width="220" fixed="right">
        <template #default="scope">
          <el-button size="small" @click="openDialog(scope.row)">编辑</el-button>
          <el-button size="small" type="danger" @click="remove(scope.row)">删除</el-button>
        </template>
      </el-table-column>
    </el-table>

    <div style="margin-top: 12px; display: flex; justify-content: flex-end;">
      <el-pagination
        layout="prev, pager, next"
        :total="total"
        :page-size="limit"
        :current-page="page"
        @current-change="pageChange"
      />
    </div>

    <el-dialog v-model="dialogVisible" title="文章编辑" width="70%" :close-on-click-modal="false">
      <el-form label-width="120px">
        <template v-if="sourceType === 'site'">
          <el-form-item label="文章名称（英文）">
            <el-input v-model="form.name" :disabled="!!form.id" placeholder="小写字母开头，如 home_intro" />
          </el-form-item>
          <el-form-item label="内容类型">
            <el-select v-model="form.contentType" style="width: 200px">
              <el-option label="Markdown" value="markdown" />
              <el-option label="HTML" value="html" />
            </el-select>
          </el-form-item>
        </template>
        <el-form-item v-else :label="sourceType === 'game' ? 'Game ID' : (sourceType === 'collection' ? '合集ID' : '分类名称')">
          <el-input v-model="form.gameId" placeholder="请输入" />
        </el-form-item>
        <el-form-item label="标题">
          <el-input v-model="form.title" placeholder="请输入文章标题" />
        </el-form-item>
        <el-tabs v-model="tab">
          <el-tab-pane name="edit" label="编辑">
            <el-input
              v-model="form.content"
              type="textarea"
              :rows="18"
              :placeholder="sourceType === 'site' && form.contentType === 'html' ? '请输入 HTML' : '请输入 Markdown'"
            />
          </el-tab-pane>
          <el-tab-pane name="preview" label="预览">
            <div class="markdown-preview" v-html="previewContent()"></div>
          </el-tab-pane>
        </el-tabs>
      </el-form>
      <template #footer>
        <el-button @click="dialogVisible = false">取消</el-button>
        <el-button type="primary" :loading="saving" @click="save">保存</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import { ElMessage, ElMessageBox } from 'element-plus';
import { ArticleApi, ArticleRow } from '../../api/articleApi';
import { CollectionApi } from '../../api/collectionApi';
import { CategoryArticleApi } from '../../api/categoryArticleApi';
import { SiteArticleApi, SiteArticleRow } from '../../api/siteArticleApi';

type Source = 'game' | 'collection' | 'category' | 'site';

export default defineComponent({
  name: 'Article',
  data() {
    return {
      loading: false,
      saving: false,
      sourceType: 'game' as Source,
      rows: [] as ArticleRow[] | SiteArticleRow[],
      total: 0,
      page: 1,
      limit: 20,
      query: {
        gameId: '',
        collectionId: '',
        categoryName: '',
        siteName: '',
        title: '',
      },
      dialogVisible: false,
      tab: 'edit',
      form: {
        id: '',
        name: '',
        gameId: '',
        title: '',
        content: '',
        contentType: 'markdown' as 'markdown' | 'html',
      },
    };
  },
  methods: {
    renderMarkdown(text: string): string {
      const escaped = (text || '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;');
      return escaped
        .replace(/^### (.*)$/gm, '<h3>$1</h3>')
        .replace(/^## (.*)$/gm, '<h2>$1</h2>')
        .replace(/^# (.*)$/gm, '<h1>$1</h1>')
        .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
        .replace(/\*(.*?)\*/g, '<em>$1</em>')
        .replace(/`([^`]+)`/g, '<code>$1</code>')
        .replace(/\n/g, '<br/>');
    },
    previewContent(): string {
      if (this.sourceType === 'site' && this.form.contentType === 'html') {
        return this.form.content || '';
      }
      return this.renderMarkdown(this.form.content);
    },
    async init(): Promise<void> {
      this.loading = true;
      try {
        if (this.sourceType === 'game') {
          const res = await ArticleApi.list((this.page - 1) * this.limit, this.limit, this.query);
          this.rows = res.list as any;
          this.total = res.total;
        } else if (this.sourceType === 'collection') {
          const res = await CollectionApi.listArticles((this.page - 1) * this.limit, this.limit, this.query);
          this.rows = (res.list as any[]).map((r) => ({ ...r, gameId: r.collectionId }));
          this.total = res.total;
        } else if (this.sourceType === 'category') {
          const res = await CategoryArticleApi.list((this.page - 1) * this.limit, this.limit, this.query);
          this.rows = (res.list as any[]).map((r) => ({ ...r, gameId: r.categoryName }));
          this.total = res.total;
        } else {
          const res = await SiteArticleApi.list((this.page - 1) * this.limit, this.limit, {
            name: this.query.siteName || undefined,
            title: this.query.title || undefined,
          });
          this.rows = res.list;
          this.total = res.total;
        }
      } finally {
        this.loading = false;
      }
    },
    reload(): void {
      this.page = 1;
      this.init();
    },
    pageChange(page: number): void {
      this.page = page;
      this.init();
    },
    openDialog(row?: ArticleRow | SiteArticleRow): void {
      this.dialogVisible = true;
      this.tab = 'edit';
      if (!row) {
        if (this.sourceType === 'site') {
          this.form = { id: '', name: '', gameId: '', title: '', content: '', contentType: 'markdown' };
        } else {
          this.form = { id: '', name: '', gameId: '', title: '', content: '', contentType: 'markdown' };
        }
        return;
      }
      if (this.sourceType === 'site') {
        const r = row as SiteArticleRow;
        this.form = {
          id: r.id,
          name: r.name,
          gameId: '',
          title: r.title,
          content: r.content,
          contentType: r.contentType === 'html' ? 'html' : 'markdown',
        };
        return;
      }
      const g = row as ArticleRow;
      this.form = { id: g.id, name: '', gameId: g.gameId, title: g.title, content: g.content, contentType: 'markdown' };
    },
    async save(): Promise<void> {
      if (this.sourceType === 'site') {
        if (!this.form.name?.trim() || !this.form.title?.trim()) {
          ElMessage.error('请填写文章名称（英文）和标题');
          return;
        }
      } else if (!this.form.gameId || !this.form.title) {
        ElMessage.error(
          this.sourceType === 'game'
            ? '请填写 gameId 和标题'
            : this.sourceType === 'collection'
              ? '请填写合集ID和标题'
              : '请填写分类名称和标题',
        );
        return;
      }
      this.saving = true;
      try {
        if (this.sourceType === 'game') {
          if (this.form.id) {
            await ArticleApi.update(this.form.id, this.form);
          } else {
            await ArticleApi.create({ gameId: this.form.gameId, title: this.form.title, content: this.form.content });
          }
        } else if (this.sourceType === 'collection') {
          if (this.form.id) {
            await CollectionApi.updateArticleById(this.form.id, {
              title: this.form.title,
              content: this.form.content,
            });
          } else {
            await CollectionApi.saveArticle(this.form.gameId, {
              title: this.form.title,
              content: this.form.content,
            });
          }
        } else if (this.sourceType === 'category') {
          if (this.form.id) {
            await CategoryArticleApi.update(this.form.id, {
              title: this.form.title,
              content: this.form.content,
            });
          } else {
            await CategoryArticleApi.saveByCategoryName(this.form.gameId, {
              title: this.form.title,
              content: this.form.content,
            });
          }
        } else {
          if (this.form.id) {
            await SiteArticleApi.update(this.form.id, {
              name: this.form.name,
              title: this.form.title,
              content: this.form.content,
              contentType: this.form.contentType,
            });
          } else {
            await SiteArticleApi.create({
              name: this.form.name.trim(),
              title: this.form.title.trim(),
              content: this.form.content,
              contentType: this.form.contentType,
            });
          }
        }
        ElMessage.success('保存成功');
        this.dialogVisible = false;
        await this.init();
      } finally {
        this.saving = false;
      }
    },
    remove(row: ArticleRow | SiteArticleRow): void {
      ElMessageBox.confirm(`确认删除文章「${row.title}」吗？`, '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning',
      })
        .then(async () => {
          if (this.sourceType === 'game') {
            await ArticleApi.remove(row.id);
          } else if (this.sourceType === 'collection') {
            await CollectionApi.removeArticleById(row.id);
          } else if (this.sourceType === 'category') {
            await CategoryArticleApi.remove(row.id);
          } else {
            await SiteArticleApi.remove(row.id);
          }
          ElMessage.success('删除成功');
          await this.init();
        })
        .catch(() => {});
    },
  },
  mounted() {
    this.init();
  },
});
</script>

<style scoped>
.markdown-preview {
  min-height: 320px;
  max-height: 60vh;
  overflow: auto;
  border: 1px solid #ebeef5;
  border-radius: 6px;
  padding: 12px;
  background-color: #fff;
  line-height: 1.7;
  color: #303133;
}
</style>
